import { PalmettoTextInput } from '@palmetto/dsm-components';
import React, { useCallback, useMemo, useState } from 'react';

import { getEvaluationCore } from 'types';
import type { EvaluationView } from 'types';

import { FlagAlertBadge } from '../ChecklistInputFlagAlertBadge';
import type { EvaluationInputComponentProps } from '../evaluationClientRegistry';
import { computeLiveEvaluationState } from '../evaluationLiveCompute';

interface ScalarEvaluationFieldConfig {
  derivedKey: string;
  enteredKey: string;
  productionReportKey: string;
  derivedLabel: string;
  enteredLabel: string;
  unit: string;
  decimals: number;
}

const roundForDisplay = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

// Signature of the view's persisted entered value, used below as a derived-state reset key
// so local edits re-seed only when the persisted view actually changes.
const enteredSignature = (view: EvaluationView | undefined, enteredKey: string): string =>
  JSON.stringify(view?.rows[0]?.entered[enteredKey] ?? null);

const SYSTEM_SIZE_CONFIG: ScalarEvaluationFieldConfig = {
  derivedKey: 'contractSystemSizeKw',
  enteredKey: 'asBuiltSizeKw',
  productionReportKey: 'asBuiltSystemSizeKw',
  derivedLabel: 'Contract',
  enteredLabel: 'As-Built System Size',
  unit: 'kW',
  decimals: 2,
};

const FIRST_YEAR_PRODUCTION_CONFIG: ScalarEvaluationFieldConfig = {
  derivedKey: 'contractFirstYearProductionKwh',
  enteredKey: 'productionModelKwh',
  productionReportKey: 'productionReportKwh',
  derivedLabel: 'Contract',
  enteredLabel: 'Production Model Value',
  unit: 'kWh',
  decimals: 0,
};

interface MetricRowProps {
  label: string;
  value: number | undefined;
  unit: string;
  decimals: number;
}

const MetricRow = ({ label, value, unit, decimals }: MetricRowProps) => (
  <div className="flex items-center justify-between py-6">
    <span className="body-300 text-text-secondary">{label}</span>
    <span className="body-300 text-text-primary">
      {value !== undefined
        ? `${value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${unit}`
        : '—'}
    </span>
  </div>
);

// Shared single-row scalar input (derived value read-only, entered value editable, live
// percent-difference chip) parameterized by field config for both System Size and
// First-Year Production.
const ScalarToleranceEvaluationInput: React.FC<
  EvaluationInputComponentProps & { config: ScalarEvaluationFieldConfig }
> = ({ label, view, readOnly, onChange, config }) => {
  const { derivedKey, enteredKey, productionReportKey, derivedLabel, enteredLabel, unit, decimals } = config;
  const row = view?.rows[0];
  const derivedValue = row?.derived[derivedKey];
  const productionReportValue = row?.derived[productionReportKey];

  // Local editable copy of the entered value — `view` is static, so binding the input to it
  // directly would leave the field frozen. Re-seeds when the view's persisted value changes.
  const [enteredValue, setEnteredValue] = useState<number | null>(() => row?.entered[enteredKey] ?? null);
  const [seededSignature, setSeededSignature] = useState<string>(() => enteredSignature(view, enteredKey));
  const currentSignature = enteredSignature(view, enteredKey);
  if (currentSignature !== seededSignature) {
    setEnteredValue(row?.entered[enteredKey] ?? null);
    setSeededSignature(currentSignature);
  }

  const core = view ? getEvaluationCore(view.key) : undefined;
  // Compute the live result from the view's derived + params and the LOCAL entered value.
  const result = useMemo(() => {
    if (!view || !core) return undefined;
    return core.computeRow(row?.derived ?? {}, { [enteredKey]: enteredValue }, view.params);
  }, [view, core, row?.derived, enteredKey, enteredValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!view) return;
      const raw = e.target.value;
      const parsed = raw === '' ? null : Number(raw);
      const nextEntered = Number.isNaN(parsed) ? null : parsed;
      setEnteredValue(nextEntered);
      const enteredByRow = view.rows.map((r, index) =>
        index === 0 ? { ...r.entered, [enteredKey]: nextEntered } : r.entered,
      );
      onChange(computeLiveEvaluationState(view, enteredByRow));
    },
    [view, onChange, enteredKey],
  );

  const percentDiff = result?.percentDifference;
  const differenceDisplay =
    percentDiff !== undefined ? `${percentDiff >= 0 ? '+' : ''}${(percentDiff * 100).toFixed(1)}%` : '—';

  const verdict = result?.verdict ?? 'incomplete';
  const badgeMessage =
    verdict === 'pass'
      ? 'Within tolerance — no flag recommended'
      : verdict === 'fail'
        ? 'Outside tolerance — rejection flag recommended'
        : 'Data unavailable';

  return (
    <div className="flex flex-col gap-8 py-4">
      <span className="body-bold-300 text-text-primary">{label}</span>

      <div className="flex flex-col">
        <MetricRow
          label={derivedLabel}
          value={derivedValue !== undefined ? roundForDisplay(derivedValue, decimals) : undefined}
          unit={unit}
          decimals={decimals}
        />
        <MetricRow
          label="Production Report"
          value={productionReportValue !== undefined ? roundForDisplay(productionReportValue, decimals) : undefined}
          unit={unit}
          decimals={decimals}
        />
      </div>

      <PalmettoTextInput
        id={`evaluation-${enteredKey}`}
        label={enteredLabel}
        type="number"
        value={enteredValue ?? ''}
        onChange={handleChange}
        disabled={readOnly}
        suffix={unit}
      />

      <div className="flex items-center justify-between">
        <span className="body-300 text-text-secondary">Difference</span>
        <span className="body-300 text-text-primary">{differenceDisplay}</span>
      </div>

      <FlagAlertBadge verdict={verdict} message={badgeMessage} />
    </div>
  );
};

export const SystemSizeEvaluationInput: React.FC<EvaluationInputComponentProps> = (props) => (
  <ScalarToleranceEvaluationInput {...props} config={SYSTEM_SIZE_CONFIG} />
);

export const FirstYearProductionEvaluationInput: React.FC<EvaluationInputComponentProps> = (props) => (
  <ScalarToleranceEvaluationInput {...props} config={FIRST_YEAR_PRODUCTION_CONFIG} />
);
